import { Outlet, Link, useSearchParams } from "react-router-dom";
import { Home, Users, Car, UserCog, Settings, ListChecks, ChevronsUpDown, Check, CalendarDays, Clock, Wrench, FileText, Receipt, MessageSquare, BellRing, Gift, ClipboardCheck, ClipboardList, Banknote, BarChart3, Truck, CircleUser } from "lucide-react";
import { NewMenu } from "@/components/layout/NewMenu";
import { NotificationBell } from "@/modules/scheduling/shared";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AppShell } from "@/components/AppShell";
import { GlobalSearch } from "@/components/pro/GlobalSearch";
import { Package, LifeBuoy, Columns3, Sun } from "lucide-react";
import { useUnreadPoll } from "@/modules/support/SupportChat";
import { StatusBadge } from "@/components/common";
import { GarageBanners } from "@/components/pro/GarageBanners";
import { GarageAvatar } from "@/components/pro/GarageAvatar";
import { useGarage } from "@/context/GarageContext";
import { t } from "@/i18n";

function ActiveGarageCard() {
  const { garage, staff, switchGarage, access, isAdmin } = useGarage();
  const card = (
    <div className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 p-3 text-left transition-colors duration-200 hover:bg-white/10">
      <GarageAvatar garage={garage} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white" data-testid="active-garage-name">{garage.name}</p>
        <p className="text-xs text-slate-400">{t(`status.${access.role}`)}</p>
      </div>
      {staff.length > 1 && <ChevronsUpDown className="h-4 w-4 text-slate-400" />}
    </div>
  );
  if (isAdmin || staff.length < 2) return <div data-testid="active-garage-card">{card}</div>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="garage-switcher-trigger" className="w-full">{card}</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60" align="start">
        <DropdownMenuLabel>{t("pro.switchGarage")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {staff.map((m) => (
          <DropdownMenuItem key={m.garage_id} onSelect={() => switchGarage(m.garage_id)} data-testid={`garage-switch-${m.slug}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: m.primary_color }} />
            <span className="ml-2 flex-1 truncate">{m.garage_name}</span>
            {m.garage_id === garage.id && <Check className="h-4 w-4 text-teal-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ProShell() {
  const { garage, isManager, activeId, access, hasFeature } = useGarage();
  const [params] = useSearchParams();
  const support = access.role === "support";
  const mech = access.role === "mechanic";
  const mgr = isManager || support;
  const billing = hasFeature("quotes_and_invoices");
  const supportUnread = useUnreadPoll("/support/unread", (d) => d.unread, isManager);
  const I = (to, key, icon, testId, extra = {}) => ({ to, label: t(key), icon, testId, ...extra });
  const messages = hasFeature("messaging") ? [I("/pro/messages", "msg.title", MessageSquare, "pro-nav-messages")] : [];
  const groups = mech ? [
    { key: "main", items: [I("/pro/my-day", "b12.day.nav", Sun, "pro-nav-my-day"), I("/pro/board", "b12.board.nav", Columns3, "pro-nav-board"),
      I("/pro/jobs", "jobs.title", Wrench, "pro-nav-jobs", { exclusive: { status: "inspecting" } }), I("/pro/planning", "sched.planning", CalendarDays, "pro-nav-planning"), ...messages,
      ...(hasFeature("inventory") ? [I("/pro/inventory", "b8.inv.title", Package, "pro-nav-inventory")] : [])] },
  ] : [
    { key: "home", items: [I("/pro", "b13.nav.home", Home, "pro-nav-dashboard", { end: true })] },
    { key: "workshop", label: t("b13.nav.workshop"), icon: Wrench, items: [I("/pro/planning", "sched.planning", CalendarDays, "pro-nav-planning"),
      I("/pro/board", "b12.board.nav", Columns3, "pro-nav-board"), I("/pro/jobs", "jobs.title", Wrench, "pro-nav-jobs", { exclusive: { status: "inspecting" } }),
      I("/pro/jobs?status=inspecting", "b13.nav.inspections", ClipboardCheck, "pro-nav-inspections"), I("/pro/my-day", "b12.day.nav", Sun, "pro-nav-my-day"),
      ...(mgr ? [I("/pro/availability", "avail.title", Clock, "pro-nav-availability")] : [])] },
    { key: "customers", label: t("b13.nav.customers"), icon: Users, items: [I("/pro/customers", "pro.nav.customers", Users, "pro-nav-customers"),
      I("/pro/vehicles", "pro.nav.vehicles", Car, "pro-nav-vehicles"), ...messages,
      ...(hasFeature("follow_ups") && mgr ? [I("/pro/followups", "fu.title", BellRing, "pro-nav-followups")] : []),
      ...(hasFeature("referral") && mgr ? [I("/pro/referrals", "ref.title", Gift, "pro-nav-referrals")] : [])] },
    { key: "sales", label: t("b13.nav.sales"), icon: Receipt, items: billing ? [I("/pro/quotes", "quotes.title", FileText, "pro-nav-quotes"),
      ...(mgr ? [I("/pro/invoices", "invoices.title", Receipt, "pro-nav-invoices", { exclusive: { status: "paid" } }), I("/pro/invoices?status=paid", "b13.nav.payments", Banknote, "pro-nav-payments"),
        I("/pro/finance", "b13.nav.finance", BarChart3, "pro-nav-finance")] : [])] : [] },
    { key: "stock", label: t("b13.nav.stock"), icon: Package, items: hasFeature("inventory") ? [I("/pro/inventory?tab=parts", "b8.inv.parts", Package, "pro-nav-inventory"),
      I("/pro/inventory?tab=suppliers", "b8.inv.suppliers", Truck, "pro-nav-suppliers"), ...(isManager ? [I("/pro/inventory?tab=orders", "b8.inv.orders", ClipboardList, "pro-nav-orders")] : [])] : [] },
  ];
  const bottom = [
    ...(mgr ? [I("/pro/settings", "pro.nav.settings", Settings, "pro-nav-settings"), I("/pro/employees", "pro.nav.employees", UserCog, "pro-nav-employees")] : []),
    ...(isManager ? [I("/pro/onboarding", "pro.nav.setup", ListChecks, "pro-nav-setup")] : []),
    I("/account/security", "b8.account.title", CircleUser, "my-account-link"),
    ...(isManager ? [I("/pro/support", "b13.nav.contact", LifeBuoy, "pro-nav-support", { badge: supportUnread || null })] : []),
  ];
  const tabs = mech ? [{ to: "/pro/my-day", label: t("b12.day.nav"), icon: Sun, testId: "tab-my-day" }, { to: "/pro/board", label: t("b13.nav.board"), icon: Columns3, testId: "tab-board" },
    ...(hasFeature("messaging") ? [{ to: "/pro/messages", label: t("msg.title"), icon: MessageSquare, testId: "tab-messages" }] : [])]
    : [{ to: "/pro", end: true, label: t("b13.nav.home"), icon: Home, testId: "tab-home" },
      { to: "/pro/board", label: t("b13.nav.workshop"), icon: Wrench, testId: "tab-workshop", match: ["/pro/board", "/pro/jobs", "/pro/planning", "/pro/my-day"] },
      { to: "/pro/customers", label: t("b13.nav.customers"), icon: Users, testId: "tab-customers", match: ["/pro/customers", "/pro/vehicles", "/pro/messages"] }];
  const bell = <NotificationBell load={() => api.get("/notifications").then((r) => r.data)} markRead={() => api.post("/notifications/read-all")} linkFor={(n) => (n.ref_kind === "job" ? `/pro/jobs/${n.ref_id}` : n.ref_kind === "quote" ? `/pro/quotes/${n.ref_id}` : n.ref_kind === "appointment" ? "/pro/planning" : n.ref_kind === "conversation" ? `/pro/messages?c=${n.ref_id}` : n.ref_kind === "support" ? "/pro/support" : ["maintenance", "proposal"].includes(n.ref_kind) ? "/pro/followups" : null)} />;
  const actions = <><span className="hidden lg:inline-flex"><NewMenu /></span><GlobalSearch />{bell}</>;
  const top = (
    <div className="space-y-2">
      <ActiveGarageCard />
      <div className="flex items-center gap-2 px-1">
        <StatusBadge status={access.subscription_status} testId="sidebar-subscription-status" />
        {support && <StatusBadge status="support" />}
      </div>
      {support && <Link to="/admin/support" className="block px-1 text-xs text-teal-300 hover:text-white" data-testid="back-to-admin-link">{t("support.backToAdmin")}</Link>}
    </div>
  );
  return (
    <AppShell groups={groups} bottom={bottom} top={top} homeTo="/pro" banners={<GarageBanners />} mobileTitle={garage.name} actions={actions} tabs={tabs} fab={<NewMenu fab />}>
      <Outlet key={activeId + (params.get("new") ? params.get("n") || "" : "")} />
    </AppShell>
  );
}
